/**
 * #357 auto top-up settings on the Credits page (workspace owner only). Ticking the box shows the settings and
 * the terms; saving with it ticked is the owner's consent. Unticking a saved auto top-up turns it off at once.
 * Hidden where the backend's rollout flag is off, unless it is already set up (so it can still be turned off).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useGetAutoTopUpMutation, useSetAutoTopUpMutation } from '@common-reducers/backend-api/backend-api';
import { fetchCreditPackagesThunk } from '@common-reducers/CreditPurchaseThunk';
import { selectCreditPackages } from '@common-reducers/CreditPurchaseReducer';
import {
  buildEnableRequest,
  centsToDollars,
  defaultPack,
  packsFrom,
  serverMessage,
  showAutoTopUp,
  type AutoTopUpFormError,
  type AutoTopUpView,
  type TopUpPack,
} from './auto-topup-form';

const usd = (cents: number) => `$${centsToDollars(cents)}`;

interface Props {
  isOwner: boolean;
}

export default function AutoTopUpSettings({ isOwner }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const creditPackages = useAppSelector(selectCreditPackages);
  const packs = useMemo(() => packsFrom(creditPackages), [creditPackages]);
  const [getAutoTopUp] = useGetAutoTopUpMutation();
  const [setAutoTopUp, { isLoading: saving }] = useSetAutoTopUpMutation();

  const [view, setView] = useState<AutoTopUpView | undefined>(undefined);
  const [ticked, setTicked] = useState(false);
  const [packId, setPackId] = useState('');
  const [threshold, setThreshold] = useState('');
  const [limit, setLimit] = useState('');
  const [formError, setFormError] = useState<AutoTopUpFormError | null>(null);
  const [saveFailed, setSaveFailed] = useState<string | null>(null);

  function load(v: AutoTopUpView) {
    setView(v);
    setTicked(v.enabled);
    setPackId(v.packItemPriceId);
    setThreshold(String(v.thresholdCredits));
    setLimit(centsToDollars(v.monthlyLimitCents));
    setFormError(null);
  }

  // Members never call it: the backend answers them 403.
  useEffect(() => {
    if (!isOwner) return;
    let live = true;
    getAutoTopUp().unwrap().then((v) => { if (live) load(v); }).catch(() => { /* hidden on error */ });
    if (creditPackages.length === 0) dispatch(fetchCreditPackagesThunk());
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  if (!view || !showAutoTopUp(view, isOwner)) return null;

  const pack: TopUpPack | null = defaultPack(packs, packId);
  const canTurnOn = view.available === true;

  async function save(request: Parameters<typeof setAutoTopUp>[0]) {
    setSaveFailed(null);
    try {
      load(await setAutoTopUp(request).unwrap());
    } catch (error) {
      setSaveFailed(serverMessage(error) ?? t('credits.autoTopUp.saveFailed', "Couldn't save auto top-up. Please try again."));
    }
  }

  function onTick(next: boolean) {
    setFormError(null);
    setSaveFailed(null);
    setTicked(next);
    if (!next && view?.enabled) void save({ enabled: false });
  }

  function onSave() {
    if (!view) return;
    const result = buildEnableRequest({ pack, thresholdCredits: threshold, limitDollars: limit, acceptTerms: ticked }, view.termsVersion);
    if (!result.ok) { setFormError(result.error); return; }
    setFormError(null);
    void save(result.request);
  }

  const errorText: Record<AutoTopUpFormError, string> = {
    pack: t('credits.autoTopUp.errorPack', 'Choose a pack.'),
    threshold: t('credits.autoTopUp.errorThreshold', 'Enter a whole number from 1 to {{max}}.', { max: pack?.credits ?? 0 }),
    limit: t('credits.autoTopUp.errorLimit', 'Enter an amount of at least {{min}}.', { min: usd(pack?.priceCents ?? 0) }),
    terms: t('credits.autoTopUp.errorTerms', 'Tick the box to accept the terms.'),
  };

  const savedPack = packs.find((p) => p.itemPriceId === view.packItemPriceId);

  return (
    <Card className="mb-4 border shadow-sm" data-testid="auto-topup-settings">
      <CardHeader className="pb-2">
        <CardTitle style={{ color: 'var(--credits-title)' }} className="text-md font-medium">
          {t('credits.autoTopUp.title', 'Auto top-up')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id="auto-topup-enabled"
            checked={ticked}
            disabled={saving || view.pending || (!ticked && !canTurnOn)}
            onCheckedChange={(c) => onTick(c === true)}
          />
          <Label htmlFor="auto-topup-enabled" className="text-sm">
            {t('credits.autoTopUp.checkbox', 'Automatically top up my credits when they run low.')}
          </Label>
        </div>

        {view.enabled && savedPack && (
          <p className="text-sm" style={{ color: 'var(--credits-text-subtle)' }} data-testid="auto-topup-status">
            {t('credits.autoTopUp.confirmation', "Auto top-up is on. We'll add {{credits}} credits ({{price}}) when your balance drops below {{threshold}}, up to {{limit}} a month.", {
              credits: savedPack.credits, price: usd(savedPack.priceCents), threshold: view.thresholdCredits, limit: usd(view.monthlyLimitCents),
            })}
            {' '}
            {t('credits.autoTopUp.spent', 'Auto top-ups this month: {{spent}} of {{limit}}.', {
              spent: usd(view.spentThisMonthCents), limit: usd(view.monthlyLimitCents),
            })}
          </p>
        )}
        {view.pending && (
          <p className="text-sm" style={{ color: 'var(--credits-text-subtle)' }}>
            {t('credits.autoTopUp.pending', 'A top-up is being charged right now.')}
          </p>
        )}
        {view.pausedUntil && new Date(view.pausedUntil).getTime() > Date.now() && (
          <p className="text-sm text-amber-600">
            {t('credits.autoTopUp.paused', "We couldn't charge your card, so auto top-up is paused until {{date}}. Update your card to keep it running.", {
              date: new Date(view.pausedUntil).toLocaleString(),
            })}
          </p>
        )}

        {ticked && canTurnOn && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="auto-topup-threshold" className="text-sm">{t('credits.autoTopUp.threshold', 'When credits drop below')}</Label>
                <Input id="auto-topup-threshold" inputMode="numeric" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">{t('credits.autoTopUp.pack', 'Add this pack')}</Label>
                <Select value={pack?.itemPriceId ?? ''} onValueChange={setPackId}>
                  <SelectTrigger data-testid="auto-topup-pack"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {packs.map((p) => (
                      <SelectItem key={p.itemPriceId} value={p.itemPriceId}>
                        {t('credits.autoTopUp.packOption', '{{credits}} credits, {{price}}', { credits: p.credits, price: usd(p.priceCents) })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="auto-topup-limit" className="text-sm">{t('credits.autoTopUp.limit', 'Monthly auto top-up limit ($)')}</Label>
                <Input id="auto-topup-limit" inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} />
                <p className="text-xs" style={{ color: 'var(--credits-text-subtle)' }}>{t('credits.autoTopUp.limitHint', 'You can raise it.')}</p>
              </div>
            </div>
            <p className="text-xs" style={{ color: 'var(--credits-text-subtle)' }} data-testid="auto-topup-terms">
              {t('credits.autoTopUp.terms', 'We will charge the card saved on your Blueticks account for the pack you chose, each time your workspace credits drop below the threshold. We never charge more than your monthly limit. When you reach the limit, we pause automatic charges and offer you the 5,000 credits / month add-on instead. Top-up credits never expire; monthly plan credits reset each billing period. You can turn this off at any time in Credits & Usage. Charges follow our refund policy.')}
            </p>
            {formError && <p className="text-sm text-red-600" role="alert">{errorText[formError]}</p>}
            <Button
              size="sm"
              onClick={onSave}
              disabled={saving || view.pending}
              style={{ backgroundColor: '#4dbdeb', color: 'var(--color-always-white)' }}
            >
              {t('credits.autoTopUp.save', 'Save')}
            </Button>
          </div>
        )}
        {saveFailed && <p className="text-sm text-red-600" role="alert">{saveFailed}</p>}
      </CardContent>
    </Card>
  );
}
